import React, { useState, useEffect } from 'react';
import api from '../../config/api';
import { resolvePartnerLogoUrl } from '../../utils/partnerLogos';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import './AdminPages.css';

const AdminPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoFilePreview, setLogoFilePreview] = useState(null);
  const [logoFileInputKey, setLogoFileInputKey] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logoUrl: '',
    websiteUrl: '',
    active: true,
    order: 0
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const response = await api.get('/partners');
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
      alert('Erreur lors du chargement des partenaires');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const urlTrim = (formData.logoUrl || '').trim();
    if (!logoFile && !urlTrim) {
      alert('Indiquez une URL de logo ou téléchargez un fichier image');
      return;
    }
    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('description', formData.description || '');
      submitData.append('websiteUrl', formData.websiteUrl || '');
      submitData.append('order', String(formData.order));
      submitData.append('active', formData.active ? 'true' : 'false');

      if (logoFile) {
        submitData.append('logo', logoFile);
      } else if (urlTrim) {
        submitData.append('logoUrl', urlTrim);
      }

      if (editingPartner) {
        await api.put(`/partners/${editingPartner._id}`, submitData);
        alert('Partenaire mis à jour avec succès');
      } else {
        await api.post('/partners', submitData);
        alert('Partenaire créé avec succès');
      }
      setShowModal(false);
      resetForm();
      fetchPartners();
    } catch (error) {
      console.error('Error saving partner:', error);
      const msg = error.response?.data?.error || error.response?.data?.message;
      alert(msg ? `Erreur : ${msg}` : 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce partenaire?')) return;
    
    try {
      await api.delete(`/partners/${id}`);
      alert('Partenaire supprimé avec succès');
      fetchPartners();
    } catch (error) {
      console.error('Error deleting partner:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      description: partner.description || '',
      logoUrl: partner.logoUrl,
      websiteUrl: partner.websiteUrl || '',
      active: partner.active,
      order: partner.order
    });
    setLogoFile(null);
    setLogoFilePreview(null);
    setLogoFileInputKey((k) => k + 1);
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      logoUrl: '',
      websiteUrl: '',
      active: true,
      order: 0
    });
    setLogoFile(null);
    setLogoFilePreview(null);
    setLogoFileInputKey((k) => k + 1);
    setEditingPartner(null);
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoFilePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setLogoFile(null);
      setLogoFilePreview(null);
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-page-header">
          <h1>Gestion des Partenaires</h1>
          <button 
            className="btn-add"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            <FaPlus /> Ajouter un partenaire
          </button>
        </div>

        {loading ? (
          <div className="loading">Chargement...</div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Logo</th>
                  <th>Nom</th>
                  <th>Site web</th>
                  <th>Statut</th>
                  <th>Ordre</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((partner) => (
                  <tr key={partner._id}>
                    <td>
                      <img 
                        src={resolvePartnerLogoUrl(partner.logoUrl) || partner.logoUrl} 
                        alt={partner.name}
                        className="table-logo"
                      />
                    </td>
                    <td>{partner.name}</td>
                    <td>
                      {partner.websiteUrl && (
                        <a href={partner.websiteUrl} target="_blank" rel="noopener noreferrer">
                          Lien
                        </a>
                      )}
                    </td>
                    <td>
                      <span className={`status ${partner.active ? 'published' : 'draft'}`}>
                        {partner.active ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td>{partner.order}</td>
                    <td className="actions">
                      <button 
                        className="btn-edit"
                        onClick={() => handleEdit(partner)}
                      >
                        <FaEdit />
                      </button>
                      <button 
                        className="btn-delete"
                        onClick={() => handleDelete(partner._id)}
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>{editingPartner ? 'Modifier le partenaire' : 'Ajouter un partenaire'}</h2>
              <form onSubmit={handleSubmit} className="admin-form">
                <div className="form-group">
                  <label>Nom *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label>URL du logo</label>
                  <p className="form-hint" style={{ fontSize: '0.85rem', color: '#666', margin: '0 0 0.5rem' }}>
                    Facultatif si vous téléversez un fichier ci-dessous (JPG ou PNG, max 5 Mo).
                  </p>
                  <input
                    type="url"
                    name="logoUrl"
                    value={formData.logoUrl}
                    onChange={handleChange}
                    placeholder="https://…"
                  />
                </div>

                <div className="form-group">
                  <label>Téléverser un logo</label>
                  <input
                    key={`logo-file-${logoFileInputKey}`}
                    type="file"
                    name="logo"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={handleLogoFileChange}
                  />
                  {logoFilePreview && (
                    <div style={{ marginTop: '1rem' }}>
                      <button
                        type="button"
                        className="btn-cancel"
                        style={{ marginBottom: '0.5rem', padding: '0.35rem 0.75rem', fontSize: '0.85rem' }}
                        onClick={() => {
                          setLogoFile(null);
                          setLogoFilePreview(null);
                          setLogoFileInputKey((k) => k + 1);
                        }}
                      >
                        Retirer le fichier
                      </button>
                      <img
                        src={logoFilePreview}
                        alt="Aperçu"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '120px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          objectFit: 'contain'
                        }}
                      />
                    </div>
                  )}
                  {!logoFilePreview && formData.logoUrl?.trim() && (
                    <div style={{ marginTop: '1rem' }}>
                      <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>Logo actuel :</p>
                      <img
                        src={resolvePartnerLogoUrl(formData.logoUrl.trim()) || formData.logoUrl}
                        alt="Logo actuel"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '120px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          objectFit: 'contain'
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Site web</label>
                  <input
                    type="url"
                    name="websiteUrl"
                    value={formData.websiteUrl}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>Ordre d'affichage</label>
                  <input
                    type="number"
                    name="order"
                    value={formData.order}
                    onChange={handleChange}
                    min="0"
                  />
                </div>

                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      name="active"
                      checked={formData.active}
                      onChange={handleChange}
                    />
                    Actif
                  </label>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-submit">
                    {editingPartner ? 'Mettre à jour' : 'Créer'}
                  </button>
                  <button 
                    type="button" 
                    className="btn-cancel"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPartners;


